import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { type SalesMetricV2 } from "../types";

export function makeSpend(): SalesMetricV2 {
  const id = "spend";
  const name = "Spend";

  return {
    id,
    name,
    fullName: name,
    description:
      "How much money was spent on this set of ads over the given time period",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeImprs({
  isDashboardV2,
}: {
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "imprs";
  const name = isDashboardV2 ? "Impressions" : "Imprs";

  return {
    id,
    name,
    fullName: name,
    description:
      "How many times your ads were served, as determined by the platform",
    category: "northbeam",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeVisits(): SalesMetricV2 {
  const id = "visits";
  const name = "Visits";

  return {
    id,
    name,
    fullName: name,
    description: "Visits to your website",
    category: "northbeam",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeNewVisits(): SalesMetricV2 {
  const id = "newVisits";
  const name = "New Visits";

  return {
    id,
    name,
    fullName: name,
    description:
      "Visits to your website from new visitors, as determined by the Northbeam Pixel",
    category: "northbeam",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRev({
  attributionModelLabel,
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "rev";

  return {
    id,
    name: isDashboardV2
      ? "Revenue"
      : accountingMode === "accrual"
      ? "Attributed Rev"
      : "Rev",
    fullName:
      "Revenue / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      accountingMode === "cash"
        ? "Total revenue during this time period"
        : `Revenue attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevFt({
  attributionModelLabel,
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revFt";

  return {
    id,
    name: isDashboardV2
      ? "Revenue (New)"
      : accountingMode === "accrual"
      ? "Attributed Rev (1st time)"
      : "Rev (1st time)",
    fullName:
      "Revenue / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      accountingMode === "cash"
        ? "Total revenue during this time period"
        : `Revenue attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevRtn({
  attributionModelLabel,
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revRtn";

  return {
    id,
    name: isDashboardV2
      ? "Revenue (Returning)"
      : accountingMode === "accrual"
      ? "Attributed Rev (Returning)"
      : "Rev (Returning)",
    fullName:
      "Revenue / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      accountingMode === "cash"
        ? "Total revenue during this time period"
        : `Revenue attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTxns({
  attributionModelLabel,
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "txns";

  return {
    id,
    name: isDashboardV2 ? "Orders" : "Transactions",
    fullName:
      "Orders / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      accountingMode === "cash"
        ? "Total orders during this time period"
        : `Orders attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTxnsFt({
  attributionModelLabel,
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "txnsFt";

  return {
    id,
    name: isDashboardV2 ? "Orders (New)" : "Transactions (1st time)",
    fullName:
      "Orders / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      accountingMode === "cash"
        ? "Total orders during this time period"
        : `Orders attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTxnsRtn({
  attributionModelLabel,
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "txnsRtn";

  return {
    id,
    name: isDashboardV2 ? "Orders (Returning)" : "Transactions (Returning)",
    fullName:
      "Orders / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      accountingMode === "cash"
        ? "Total orders during this time period"
        : `Orders attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueWebShops({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "revenueWebShops";

  return {
    id,
    name: "Web Revenue",
    fullName:
      "Web Revenue / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Revenue attributed to your ads in Web shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueMetaShops({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "revenueMetaShops";

  return {
    id,
    name: "Meta Shops Revenue",
    fullName:
      "Meta Shops Revenue / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Revenue attributed to your ads in Meta shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueTikTokShops({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "revenueTikTokShops";

  return {
    id,
    name: "TikTok Shops Revenue",
    fullName:
      "TikTok Shops Revenue / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Revenue attributed to your ads in TikTok shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsWebShops({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsWebShops";

  return {
    id,
    name: isDashboardV2 ? "Web Orders" : "Web Transactions",
    fullName:
      "Web Orders / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Orders attributed to your ads in Web shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsMetaShops({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsMetaShops";

  return {
    id,
    name: isDashboardV2 ? "Meta Shops Orders" : "Meta Shops Transactions",
    fullName:
      "Meta Shops Orders / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Orders attributed to your ads in Meta shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsTikTokShops({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsTikTokShops";

  return {
    id,
    name: isDashboardV2 ? "TikTok Shops Orders" : "TikTok Shops Transactions",
    fullName:
      "TikTok Shops Orders / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Orders attributed to your ads in TikTok shops",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscounts({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderDiscounts";

  return {
    id,
    name: "Discounts",
    fullName:
      "Discounts / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to " +
      "orders (or PROMOS if this applied to Amazon) as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscountsFt({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsFt";

  return {
    id,
    name: isDashboardV2 ? "Discounts (New)" : "Discounts (1st time)",
    fullName:
      "Discounts / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to first time " +
      "customer orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscountsRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderDiscountsRtn";

  return {
    id,
    name: "Discounts (Returning)",
    fullName:
      "Discounts / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to returning " +
      "customer orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValue({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "taxValue";

  return {
    id,
    name: "Tax",
    fullName:
      "Tax / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValueFt({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueFt";

  return {
    id,
    name: isDashboardV2 ? "Tax (New)" : "Tax (1st time)",
    fullName:
      "Tax / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to first time customer " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValueRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "taxValueRtn";

  return {
    id,
    name: "Tax (Returning)",
    fullName:
      "Tax / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to returning customer " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValue({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "shippingValue";

  return {
    id,
    name: "Customer Shipping",
    fullName:
      "Customer Shipping / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValueFt({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueFt";

  return {
    id,
    name: isDashboardV2
      ? "Customer Shipping (New)"
      : "Customer Shipping (1st time)",
    fullName:
      "Customer Shipping / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from first time customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValueRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "shippingValueRtn";

  return {
    id,
    name: "Customer Shipping (Returning)",
    fullName:
      "Customer Shipping / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from returning customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValue({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderRefundedValue";

  return {
    id,
    name: "Refunds",
    fullName:
      "Refunds / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValueFt({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueFt";

  return {
    id,
    name: isDashboardV2 ? "Refunds (New)" : "Refunds (1st time)",
    fullName:
      "Refunds / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from " +
      "first time customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValueRtn({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderRefundedValueRtn";

  return {
    id,
    name: "Refunds (Returning)",
    fullName:
      "Refunds / Ret" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from " +
      "returning customer orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueOrderSubscription({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscription";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue"
      : accountingMode === "accrual"
      ? "Subscription attributed revenue"
      : "Subscription revenue",
    fullName:
      "Subscription Revenue / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Includes all subscription categories",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueOrderSubscriptionFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (New)"
      : accountingMode === "accrual"
      ? "Subscription attributed revenue (1st time)"
      : "Subscription revenue (1st time)",
    fullName:
      "Subscription Revenue / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Includes order tags you've mapped to the 'first time' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueOrderSubscriptionRecurring({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionRecurring";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (Recurring)"
      : accountingMode === "accrual"
      ? "Subscription attributed revenue (Recurring)"
      : "Subscription revenue (Recurring)",
    fullName:
      "Subscription Revenue / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Includes order tags you've mapped to the 'recurring' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeRevenueOrderSubscriptionOther({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "revenueOrderSubscriptionOther";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Revenue (Other)"
      : accountingMode === "accrual"
      ? "Subscription attributed revenue (Other)"
      : "Subscription revenue (Other)",
    fullName:
      "Subscription Revenue / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Includes all order tags that were mapped to 'other', or could not be mapped to " +
      "'recurring' or 'first time'",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsOrderSubscription({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscription";

  return {
    id,
    name: isDashboardV2 ? "Subscription Orders" : "Subscription transactions",
    fullName:
      "Subscription Orders / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description: "Includes all subscription categories",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsOrderSubscriptionFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (New)"
      : "Subscription transactions (1st time)",
    fullName:
      "Subscription Orders / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Includes order tags you've mapped to the 'first time' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsOrderSubscriptionRecurring({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionRecurring";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (Recurring)"
      : "Subscription transactions (Recurring)",
    fullName:
      "Subscription Orders / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Includes order tags you've mapped to the 'recurring' category",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTransactionsOrderSubscriptionOther({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "transactionsOrderSubscriptionOther";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Orders (Other)"
      : "Subscription transactions (Other)",
    fullName:
      "Subscription Orders / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Includes all order tags that were mapped to 'other', or could not be mapped to " +
      "'recurring' or 'first time'",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscountsOrderSubscriptions({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptions";

  return {
    id,
    name: "Subscription Discounts",
    fullName:
      "Subscription Discounts / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to subscription " +
      "orders (or PROMOS if this applied to Amazon) as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscountsOrderSubscriptionsFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Discounts (New)"
      : "Subscription Discounts (1st time)",
    fullName:
      "Subscription Discounts / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to first time customer " +
      "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscountsOrderSubscriptionsRecurring({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsRecurring";

  return {
    id,
    name: "Subscription Discounts (Recurring)",
    fullName:
      "Subscription Discounts / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to recurring customer " +
      "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderDiscountsOrderSubscriptionsOther({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderDiscountsOrderSubscriptionsOther";

  return {
    id,
    name: "Subscription Discounts (Other)",
    fullName:
      "Subscription Discounts / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Value of all discount codes and automatic discounts applied to other customer " +
      "subscription orders (or PROMOS if this applied to Amazon) as reported by your " +
      "ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValueOrderSubscriptions({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptions";

  return {
    id,
    name: "Subscription Tax",
    fullName:
      "Subscription Tax / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValueOrderSubscriptionsFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Tax (New)"
      : "Subscription Tax (1st time)",
    fullName:
      "Subscription Tax / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to first time customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValueOrderSubscriptionsRecurring({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsRecurring";

  return {
    id,
    name: "Subscription Tax (Recurring)",
    fullName:
      "Subscription Tax / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to recurring customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTaxValueOrderSubscriptionsOther({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "taxValueOrderSubscriptionsOther";

  return {
    id,
    name: "Subscription Tax (Other)",
    fullName:
      "Subscription Tax / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "The sum of all the taxes applied to other customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValueOrderSubscriptions({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptions";

  return {
    id,
    name: "Customer Subscription Shipping",
    fullName:
      "Customer Subscription Shipping / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValueOrderSubscriptionsFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Customer Subscription Shipping (New)"
      : "Customer Subscription Shipping (1st time)",
    fullName:
      "Customer Subscription Shipping / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from first time customer subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValueOrderSubscriptionsRecurring({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsRecurring";

  return {
    id,
    name: "Customer Subscription Shipping (Recurring)",
    fullName:
      "Customer Subscription Shipping / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from recurring customer subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeShippingValueOrderSubscriptionsOther({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "shippingValueOrderSubscriptionsOther";

  return {
    id,
    name: "Customer Subscription Shipping (Other)",
    fullName:
      "Customer Subscription Shipping / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Shipping fees charged to customers (i.e. FREE Shipping is not included) " +
      "from other customer subscription orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValueOrderSubscriptions({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptions";

  return {
    id,
    name: "Subscription Refunds",
    fullName:
      "Subscription Refunds / All" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValueOrderSubscriptionsFirstTime({
  accountingMode,
  attributionWindow,
  isDashboardV2,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsFirstTime";

  return {
    id,
    name: isDashboardV2
      ? "Subscription Refunds (New)"
      : "Subscription Refunds (1st time)",
    fullName:
      "Subscription Refunds / New" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from first time customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValueOrderSubscriptionsRecurring({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsRecurring";

  return {
    id,
    name: "Subscription Refunds (Recurring)",
    fullName:
      "Subscription Refunds / Recurring" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from recurring customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeOrderRefundedValueOrderSubscriptionsOther({
  accountingMode,
  attributionWindow,
}: {
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = "orderRefundedValueOrderSubscriptionsOther";

  return {
    id,
    name: "Subscription Refunds (Other)",
    fullName:
      "Subscription Refunds / Other" +
      (accountingMode === "accrual" ? ` (${attributionWindow}d)` : ""),
    description:
      "Returns, refunds, AND cancellations from other customer subscription " +
      "orders as reported by your ecommerce platform(s).",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
